import { Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { ErrorCatcher } from '../../../../../../tools/error-catcher';
import { Heading } from '../../../../../../ui-components/heading/heading';
import { Text } from '../../../../../../ui-components/text/text';
import { HeroComponentProps } from '../../../../../hero/hero-types';
import { ImageSrcData, getBackgroundImageSrcData } from '../../../../../image-component/image-component';
import { SpacerPdf } from '../spacer/spacer-pdf';
import { focusPosition } from '../../../../../../tools/get-image-focus';
import { AppContext } from '../../../../../../app-context-provider/app-context-provider';
import { prettifyUrl } from '../../pretty-url';
import theme from '../../../../../../style/theme';
import { availableWidth } from '../../pdf-constants';

export const HeroPdf = (props: HeroComponentProps) => {
    const { imagesServiceUrl, supportedImageFormats, siteInfo } = useContext(AppContext);

    const getBackgroundImage = () => {
        if (props.src) {
            const backgroundImage: ImageSrcData = getBackgroundImageSrcData({
                imagesServiceUrl,
                supportedImageFormats,
                src: props.src,
                width: 1500,
                height: 'AUTO',
            });
            return backgroundImage;
        }
    };

    const bgImage = () => getBackgroundImage();
    const hasBgImage = () => bgImage() && bgImage()!.original !== '';

    return (
        <ErrorCatcher componentName='Hero-PDF'>
            <div>
                <div
                    class={clsx(
                        'relative p-10 pt-px min-h-[21rem] rounded-[0.73rem] mr-[-125px]',
                        hasBgImage() && 'pdf-beige-overlay [&::before]:opacity-45 bg-no-repeat bg-cover'
                    )}
                    style={hasBgImage() ? {
                        'background-image': `url(${bgImage()!.original})`,
                        'background-position': focusPosition(props.focus) || 'center',
                    } : undefined}
                >
                    <div class="relative z-0 w-[85%]">
                        <Show when={props.eyebrowHeading} fallback={<SpacerPdf height={1.45}/>}>
                            <SpacerPdf height={2.5} />
                            <Heading tag={'h1'} variant='heroEyebrowPDF'>{props.eyebrowHeading}</Heading>
                        </Show>

                        <Heading tag={'h2'} variant='heroPDF'>{props.largerHeading}</Heading>

                        <Show when={props.description}>
                            <div class="w-4/5">
                                <Text fontSize="largestPDF" displayRedVerticalLine={false} noBlockSpacing={false} color='black'>
                                    {props.description}
                                </Text>
                            </div>
                        </Show>

                        <Show when={props.customLink?.url}>
                            <SpacerPdf height={2.5} />
                            <div class="flex items-center">
                                <Text
                                    fontSize="largerPDF"
                                    displayRedVerticalLine={false}
                                    noBlockSpacing={true}
                                    color={theme.palette.primary}
                                    underlined={true}
                                >
                                    {props.customLink?.label}
                                </Text>
                                <div class="pl-2">
                                    <Text
                                        fontSize="largePDF"
                                        displayRedVerticalLine={false}
                                        noBlockSpacing={true}
                                        fontStyle='italic'
                                    >
                                        {`(${prettifyUrl(props.customLink?.url, siteInfo)})`}
                                    </Text>
                                </div>
                            </div>
                        </Show>
                    </div>

                </div>
                <SpacerPdf height={4.5} />
            </div>
        </ErrorCatcher>
    );
};

HeroPdf.parseProps = (atts: any) => {
    const { image } = atts;

    const commonAtts = {
        altText: atts.altText,
        imageText: atts.imageText,
        eyebrowHeading: atts.eyebrowHeading,
        largerHeading: atts.largerHeading,
        description: atts.description,
        labels: atts.labels,
        customLink: atts.customLink?.[0],
    };

    if (image) {
        return {
            src: image.url,
            width: image.width,
            focus: image.focus,
            overlayType: atts.overlayType,
            ...commonAtts,
        };
    } else return commonAtts;
};
